import { useCallback, memo } from 'react'
import { usePhone } from '@finanzcheck/catalyst-renderless-components'
import type { CountryCode, PhoneNumber } from 'libphonenumber-js'
import Text, { TextProps } from './Text'

interface OwnProps {
  value?: string
  defaultRegion?: CountryCode
  onChange?: (phoneNumber: PhoneNumber | null, raw?: string) => void
  onBlur?: (phoneNumber: PhoneNumber | null, raw?: string) => void
  onFocus?: (phoneNumber: PhoneNumber | null, raw?: string) => void
}

export type PhoneInputProps = Omit<
  TextProps,
  'onChange' | 'onBlur' | 'onFocus' | 'value' | 'defaultValue'
> &
  OwnProps

const Phone = ({
  value = '',
  error: propError,
  onChange,
  onBlur,
  onFocus,
  name = 'phone',
  id,
  autoComplete,
  label,
  onTooltip,
  tooltip,
  placeholder,
  outerLabel,
  smallTextField,
  showTooltipOnFieldClick,
  isTooltipHidden,
  scrollOnFocusEnabled
}: PhoneInputProps) => {
  const blurHandler = useCallback(
    (phoneNumber: PhoneNumber | null) => (raw: string) => {
      if (onBlur) {
        onBlur(phoneNumber, raw)
      }
    },
    [onBlur]
  )
  const focusHandler = useCallback(
    (phoneNumber: PhoneNumber | null) => (raw: string) => {
      if (onFocus) {
        onFocus(phoneNumber, raw)
      }
    },
    [onFocus]
  )
  const changeHandler = useCallback(
    (phoneNumber: PhoneNumber | null) => (raw: string) => {
      if (onChange) {
        onChange(phoneNumber, raw)
      }
    },
    [onChange]
  )

  const { ref, phoneNumber, getProps, value: phoneValue } = usePhone({ value })

  return (
    <Text
      {...getProps({ name, id })}
      value={phoneValue}
      onBlur={blurHandler(phoneNumber)}
      onFocus={focusHandler(phoneNumber)}
      onChange={changeHandler(phoneNumber)}
      ref={ref}
      error={propError}
      acceptedInput={/[0-9]|\+/}
      autoComplete={autoComplete}
      label={label}
      helperText={null}
      onTooltip={onTooltip}
      tooltip={tooltip}
      placeholder={placeholder}
      outerLabel={outerLabel}
      smallTextField={smallTextField}
      showTooltipOnFieldClick={showTooltipOnFieldClick}
      isTooltipHidden={isTooltipHidden}
      scrollOnFocusEnabled={scrollOnFocusEnabled}
    />
  )
}

export default memo(
  Phone,
  (prevProps, nextProps) =>
    JSON.stringify(prevProps) === JSON.stringify(nextProps)
)
